import { toast } from 'react-toastify';

// Logging levels
enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

// Current log level (can be set via environment variable)
const CURRENT_LOG_LEVEL = 
  import.meta.env.VITE_LOG_LEVEL === 'debug' ? LogLevel.DEBUG :
  import.meta.env.VITE_LOG_LEVEL === 'info' ? LogLevel.INFO :
  import.meta.env.VITE_LOG_LEVEL === 'warn' ? LogLevel.WARN :
  import.meta.env.VITE_LOG_LEVEL === 'error' ? LogLevel.ERROR :
  LogLevel.INFO; // Default to INFO

// Whether to show toasts for each log type
const SHOW_TOASTS = {
  [LogLevel.DEBUG]: false,
  [LogLevel.INFO]: false,
  [LogLevel.WARN]: true,
  [LogLevel.ERROR]: true,
};

// Track error occurrences to avoid spamming
const errorOccurrences: Record<string, { count: number; lastTime: number }> = {};

/**
 * Log a message with optional data
 */
function log(level: LogLevel, message: string, data?: any) {
  // Skip if the current log level is higher than this message
  if (level < CURRENT_LOG_LEVEL) return;

  // Format log message
  const timestamp = new Date().toISOString();
  const messageWithData = data ? `${message}: ${JSON.stringify(data)}` : message;

  // Get log method based on level
  let logMethod: 'log' | 'info' | 'warn' | 'error' = 'log';
  let toastMethod: 'info' | 'success' | 'warn' | 'error' = 'info';

  switch (level) {
    case LogLevel.DEBUG:
      logMethod = 'log';
      toastMethod = 'info';
      break;
    case LogLevel.INFO:
      logMethod = 'info';
      toastMethod = 'success';
      break;
    case LogLevel.WARN:
      logMethod = 'warn';
      toastMethod = 'warn';
      break;
    case LogLevel.ERROR:
      logMethod = 'error';
      toastMethod = 'error';
      break;
  }

  // Log to console
  console[logMethod](`[${timestamp}] ${messageWithData}`);

  // Show toast notification if enabled for this level
  if (SHOW_TOASTS[level]) {
    // For errors, check if we've shown this one recently to avoid spamming
    if (level === LogLevel.ERROR) {
      const errorKey = messageWithData.slice(0, 100); // Use first 100 chars as key
      const now = Date.now();
      const errorData = errorOccurrences[errorKey] || { count: 0, lastTime: 0 };
      
      // If we've shown this error recently, just increment the count
      if (now - errorData.lastTime < 5000) { // 5 seconds
        errorOccurrences[errorKey] = {
          count: errorData.count + 1,
          lastTime: now,
        };
        return;
      }
      
      // If we've shown this error before but not recently, include the count
      if (errorData.count > 0) {
        toast[toastMethod](
          `${message} (occurred ${errorData.count + 1} times)`, 
          { toastId: errorKey }
        );
      } else {
        toast[toastMethod](message, { toastId: errorKey });
      }
      
      // Reset the counter
      errorOccurrences[errorKey] = { count: 0, lastTime: now };
    } else {
      toast[toastMethod](message);
    }
  }
}

// Export convenience methods
export const logger = {
  debug: (message: string, data?: any) => log(LogLevel.DEBUG, message, data),
  info: (message: string, data?: any) => log(LogLevel.INFO, message, data),
  warn: (message: string, data?: any) => log(LogLevel.WARN, message, data),
  error: (message: string, data?: any) => log(LogLevel.ERROR, message, data),
};

// Export for direct use (e.g. in third-party code)
export default logger; 